import React, { useRef, useState } from "react";
import { Avatar, Box, Stack, TextField, InputAdornment, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as SearchIcon } from '../../../../assets/images/TicketImages/search.svg';
import { colorCodesForAvatar } from "../../../../constants/index";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import useClickOutside from "../../../../hooks/useClickOutside";
import NoSearch from "../../../common/NoSearch";
import DropdownSelectionTick from "../../People/DropdownSelectionTick";

const TeamMemberSelect = ({
  value,
  onChange,
  triggerStyles,
  teamMembers = [],
  placeholder = "Assign",
  dropdownWidth = '100%',
  dropdownHeight,
  showSelectionTick = false,
  hideEmail = false,
  tickOnlyHighlight = false,
  wrapperStyle = {},
  dropDownTop = 'calc(100% + 8px)',
  width = "100% ", // Default width
  /** When set, shows an "Assign to me" row that selects this member id */
  assignToMeMemberId = null,
  showAddPeopleFooter = false,
  onAddPeople,
  /** When false, member rows show avatar + name only (e.g. TicketV2 assignee). */
  showMemberEmailInDropdown = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useClickOutside(wrapperRef, () => {
    setOpen(false);
    setSearchTerm("");
  });

  const q = searchTerm.toLowerCase();
  const filteredMembers = teamMembers.filter((member) => {
    const name = (member.full_name || "").toLowerCase();
    const email = (member.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const selectedMember = teamMembers.find((member) => member._id === value);

  const getAvatarColor = (name) => {
    if (!name) return colorCodesForAvatar[0];
    const first = name[0].toUpperCase();
    const idx = first.charCodeAt(0) - 65;
    return colorCodesForAvatar[
      idx >= 0 && idx < colorCodesForAvatar.length ? idx : 0
    ];
  };

  const handleSelect = (memberId) => {
    onChange({ target: { value: memberId } });
    setOpen(false);
  };

  // Styles
  const styles = {
    wrapper: {
      position: "relative",
      display: "inline-block",
      // width: width,
      minWidth: '128px',
      ...wrapperStyle,
    },
    trigger: {
      borderRadius: "8px",
      border: "1px solid #D5DCE1",
      background: "#FFF",
      boxShadow: "0px 1px 2px rgba(6, 21, 34, 0.05)",
      height: "40px",
      padding: "0 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      cursor: "pointer",
      width: "100%",
      overflow: "hidden",
      ...triggerStyles,
    },
    triggerText: {
      color: selectedMember ? "#111827" : "#405261",
      fontSize: "14px",
      fontFamily: "Inter",
      fontWeight: 400,
      textTransform: "capitalize",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,
      minWidth: 0,
    },
    dropdown: {
      position: "absolute",
      top: dropDownTop,
      left: 0,
      zIndex: 999,
      border: "1px solid #E5E7EB",
      borderRadius: "8px",
      padding: "8px",
      boxSizing: "border-box",
      width: dropdownWidth,
      ...(dropdownHeight
        ? { height: dropdownHeight, maxHeight: dropdownHeight }
        : { maxHeight: "250px" }),

      backgroundColor: "#FFFFFF",
      boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      overflowX: "hidden",
      overflowY: "hidden",
    },
    searchContainer: {
      position: "sticky",
      top: 0,
      backgroundColor: "#fff",
      zIndex: 2,
      flexShrink: 0,
      minWidth: 0,
      overflow: "hidden",
    },
    listContainer: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      marginTop: "8px",
      overflowX: "hidden",
      overflowY: "auto",
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(0,0,0,0.2) transparent",
    },
    memberItem: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      height: hideEmail ? "40px" : "64px",
      borderRadius: "8px",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#F9FAFA",
        borderRadius: "8px",
      },
    },
    noResults: {
      color: "#6B7280",
      fontSize: "14px",
      py: 1,
      textAlign: "center",
    }
  };

  return (
    <Box ref={wrapperRef} sx={styles.wrapper}>
      {/* Trigger */}
      <Box
        sx={styles.trigger}
        onClick={() => setOpen(!open)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            overflow: "hidden",
            flex: 1,
            minWidth: 0,
          }}
        >
          {selectedMember && (
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "12px",
                flexShrink: 0,
                bgcolor: selectedMember.agent_image
                  ? "transparent"
                  : getAvatarColor(selectedMember.full_name),
              }}
              src={selectedMember.agent_image}
            >
              {!selectedMember.agent_image && selectedMember.full_name.charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Box component="span" sx={styles.triggerText}>
            {selectedMember?.full_name || placeholder}
          </Box>
        </Box>
        <Dropdown
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.2s",
            width: "16px",
            height: "16px",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Dropdown */}
      {open && (
        <Box sx={styles.dropdown}>
          {/* Search */}
          <Box sx={styles.searchContainer}>
            <TextField
              fullWidth
              autoFocus
              variant="outlined"
              placeholder="Search team member"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ width: 16, height: 16 }} />
                  </InputAdornment>
                ),
                sx: {
                  "& fieldset": { border: "1px solid #D5DCE1" },
                  "& input": {
                    height: "40px",
                    padding: "0 8px",
                    fontSize: "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    color: "#111827 !important",
                    cursor: "text !important",
                    backgroundColor: "transparent",
                    "&::placeholder": {
                      color: "#C5CED6",
                      opacity: 1,
                      fontSize: "14px",
                      fontFamily: "Inter",
                      fontWeight: 400,
                    },
                  },
                },
              }}
              sx={{
                maxWidth: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  maxWidth: "100%",
                  "&.Mui-focused fieldset": { border: "1px solid #3B82F6" },
                },
              }}
            />
          </Box>

          {/* Scrollable list: assign-to-me + members (no horizontal scroll) */}
          <Box sx={styles.listContainer}>
            {assignToMeMemberId && (
              <Box
                onClick={() => handleSelect(assignToMeMemberId)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 2,
                  py: 1.25,
                  borderRadius: "8px",
                  cursor: "pointer",
                  minWidth: 0,
                  overflow: "hidden",
                  "&:hover": { bgcolor: "#F9FAFA" },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "1px solid #E5E9EC",
                    bgcolor: "#FFF",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: 20, color: "#7F94AE" }} />
                </Box>
                <Typography
                  noWrap
                  sx={{ fontSize: 14, fontWeight: 500, color: "#061522", fontFamily: "Inter", minWidth: 0 }}
                >
                  Assign to me
                </Typography>
              </Box>
            )}

            {filteredMembers.length > 0 ? (
              filteredMembers.map((member) => (
                <Box
                  key={member._id}
                  sx={{
                    ...styles.memberItem,
                    ...(!tickOnlyHighlight &&
                      value === member._id && {
                        backgroundColor: "#F3FBFF !important",
                        border: "1px solid #148DFD",
                      }),
                  }}
                  onClick={() => handleSelect(member._id)}
                >
                  <Avatar
                    sx={{
                      width: hideEmail ? 24 : 32,
                      height: hideEmail ? 24 : 32,
                      fontSize: hideEmail ? 12 : 14,
                      bgcolor: member.agent_image ? "transparent" : getAvatarColor(member.full_name),
                    }}
                    src={member.agent_image}
                  >
                    {!member.agent_image && member.full_name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={showSelectionTick ? { flex: 1, minWidth: 0 } : undefined}>
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#061522",
                        fontFamily: "Inter",
                        fontWeight: 500,
                        textTransform: "capitalize",
                      }}
                    >
                      {member.full_name}
                    </Box>
                    {!hideEmail && (
                      <Box
                        sx={{
                          fontSize: "14px",
                          color: "#556575",
                          fontFamily: "Inter",
                          fontWeight: 400,
                        }}
                      >
                        {member.email}
                      </Box>
                    )}
                  </Box>
                  {showSelectionTick && value === member._id && <DropdownSelectionTick />}
                </Box>
              ))
            ) : (
              <Box sx={{ maxWidth: "100%", overflow: "hidden", minWidth: 0 }}>
                <NoSearch
                  title="No member found"
                  description="Clear your search and try again."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              </Box>
            )}

            {showAddPeopleFooter && typeof onAddPeople === "function" && (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddPeople();
                  setOpen(false);
                  setSearchTerm("");
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 0.75,
                  mt: 0.5,
                  py: 1.25,
                  px: 1,
                  border: 0,
                  borderTop: "1px solid #F1F3F5",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  width: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              >
                <AddIcon sx={{ fontSize: 18, color: "#148DFD", flexShrink: 0 }} />
                <Typography
                  noWrap
                  sx={{ fontSize: 14, fontWeight: 500, color: "#148DFD", fontFamily: "Inter", textAlign: "left" }}
                >
                  Add more people
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TeamMemberSelect;